/**
 * @fileoverview Input/output schemas for the single-chapter-drafter agent.
 *
 * This agent drafts a complete short story in a single chapter. Unlike chapter-drafter-v2
 * which writes one chapter of a multi-chapter narrative, this agent produces a self-contained
 * story with a full narrative arc: setup, confrontation, and resolution.
 *
 * Schema differences from chapter-drafter-v2:
 * - Removed: lastThreeChaptersContent (no preceding chapters)
 * - Removed: nextChapterContext (no next chapter)
 * - Added: plotThreadStatus (planned thread questions as narrative goals)
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.scenes.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterPovCharacter.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.styleGuide.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.draftingGenreGuidance.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.seriesContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.latestKnownPlotState.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.charactersCurrent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.worldElements.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.plotThreadStatus.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.isShortForm.schema.shape,
}).partial({
  scenes: true,
  plotThreadStatus: true,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Unified output schema for content-producing agents.
 */
export const OutputSchema = z.object({
  commentary: z.string().describe(
    'Brief commentary explaining the creative choices, changes made, or approach taken (2-3 sentences). ' +
    'This will be recorded in the drafting history.'
  ),
  content: z.string().describe(
    'The generated or revised prose, formatted as a single HTML string.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
